import {Group, Loader, Stack, ThemeIcon, Text, Title} from "@mantine/core";
import {ThreadInfo} from "../../../types";
import classes from "./RecentSearches.module.css";
import {useTranslation} from "react-i18next";
import IconArrowRight from "../../../assets/arrow-right.svg?react";
import IconMessageCircle from "../../../assets/message-circle.svg?react";
import {useNavigate} from "react-router-dom";
import * as Sentry from "@sentry/react";
import {bffApi} from "../bffApi";
import {useAppSelector} from "../../../app/hooks";
import {
    selectCompletedBackgroundStreams,
    selectStreamingThreadIds,
    threadsSelectors,
} from "../threadsSlice";
import {useMemo} from "react";

const MAX_ITEMS = 3;

export const RecentThreads = ({data}: {data: ThreadInfo[]}) => {
    const {t} = useTranslation();

    if (!data || data.length === 0) {
        return null;
    }

    return (
        <Stack gap={12} className={classes.recentSearchesList}>
            <Group gap="xs" wrap="nowrap">
                <ThemeIcon variant="subtle" size="sm" color="black">
                    <IconMessageCircle width={24} />
                </ThemeIcon>
                <Title size="lg" fw={500}>
                    {t("common.recentThreads")}
                </Title>
            </Group>
            <Stack gap={0}>
                {data.slice(0, MAX_ITEMS).map((thread) => (
                    <RecentThreadItem key={thread.threadId} thread={thread} />
                ))}
            </Stack>
        </Stack>
    );
};

interface RecentThreadItemProps {
    thread: ThreadInfo;
}

const RecentThreadItem = ({thread}: RecentThreadItemProps) => {
    const navigate = useNavigate();
    const streamingThreadIds = useAppSelector(selectStreamingThreadIds);
    const completedBackgroundStreamIds = useAppSelector(
        selectCompletedBackgroundStreams
    );
    const allThreadEntities = useAppSelector(threadsSelectors.selectAll);

    const isStreaming = streamingThreadIds.includes(thread.threadId);
    const isCompleted = useMemo(
        () =>
            completedBackgroundStreamIds.some((eid) => {
                const entity = allThreadEntities.find((e) => e.id === eid);
                return entity?.realThreadId === thread.threadId;
            }),
        [completedBackgroundStreamIds, allThreadEntities, thread.threadId]
    );

    const [trackOpened] = bffApi.endpoints.trackOpened.useMutation();

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        Sentry.metrics.count("chat.frontpage_thread_opened");
        trackOpened({
            entity: "thread",
            key: thread.threadId,
            source: "frontpage",
        });
        navigate(`/chat/${thread.threadId}`);
    };

    return (
        <a
            href={`/chat/${thread.threadId}`}
            onClick={handleClick}
            className={classes.recentSearchLink}
        >
            <Group
                gap="xs"
                wrap="nowrap"
                className={classes.recentSearchItem}
                justify="space-between"
                style={{width: "100%"}}
            >
                <Group gap="xs" wrap="nowrap">
                    {isStreaming && <Loader size="xs" type="dots" />}
                    {!isStreaming && isCompleted && (
                        <span
                            aria-label="new response"
                            style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor:
                                    "var(--mantine-color-blue-5)",
                                flex: "0 0 auto",
                            }}
                        />
                    )}
                    <Text size="sm">{thread.name}</Text>
                </Group>
                <Group gap={3} wrap="nowrap" align="center">
                    <ThemeIcon variant="subtle" size="sm" color="gneis.8">
                        <IconArrowRight
                            className={classes.recentSearchChevron}
                            width={16}
                        />
                    </ThemeIcon>
                </Group>
            </Group>
        </a>
    );
};
